<template>
  <div>
    <div class="show__heading">
      <div @click="cancelButtonHandler" class="show__back">
        <v-icon>mdi-chevron-left</v-icon>
      </div>
      <h1>{{ $t("routes.purchase_domain") }}</h1>
      <div class="ml-auto d-flex pl-4">
        <v-btn @click="onPurchase" color="primary" :disabled="selectedDomains.length <= 0">
          {{ $t("resources.domains.purchase") + ` (${selectedDomains.length})` }}
        </v-btn>
      </div>
    </div>
    <v-card class="spacing-bottom--16">
      <v-card-title>{{ $t("resources.domains.purchase_choose_name_title") }}</v-card-title>
      <v-card-subtitle>{{ $t("resources.domains.purchase_choose_name_subtitle") }}</v-card-subtitle>
      <v-form ref="form" @submit.prevent="searchDomain">
        <div class="input-wrapper">
          <v-text-field
            :label="$t('resources.domains.purchase_domain_name')"
            :placeholder="$t('resources.domains.purchase_domain_name_placeholder')"
            class="input-prefixed"
            persistent-placeholder
            hide-details="auto"
            :rules="[rules.required]"
            :error-messages="errors"
            v-model="query">
            <template #prepend-inner>
              <v-icon>{{ mdiSearchWeb }}</v-icon>
            </template>
          </v-text-field>
          <v-btn color="primary" outlined class="purchase-btn" @click="searchDomain" :loading="loading">
            {{ $t("resources.domains.purchase_domain_generate") }}
          </v-btn>
        </div>
      </v-form>
    </v-card>
    <v-card class="spacing-bottom--16" v-if="domains.length > 0">
      <v-card-title>{{ $t("resources.domains.purchase_domain_results") }}</v-card-title>
      <div class="domain-results-wrapper">
        <div v-for="domain in domains" :key="domain['@id']" class="domain-results">
          <div>
            {{ getDomainName(domain.domain).firstPart }}<b>{{ "." + getDomainName(domain.domain).secondPart }}</b>
          </div>
          <div>
            <span class="domain-price">
              {{
                domain.available
                  ? domain.price.toLocaleString("en-US", { style: "currency", currency: "USD" }) + "/year"
                  : $t("resources.domains.purchase_domain_unavailable")
              }}
            </span>
            <v-btn
              v-if="domain.available && !domain.selected"
              class="select-button"
              color="primary"
              outlined
              @click="selectDomain(domain)"
              >{{ $t("resources.domains.purchase_domain_select") }}</v-btn
            >
            <v-btn
              v-else-if="domain.available && domain.selected"
              class="select-button"
              color="primary"
              @click="removeDomain(domain)"
              >{{ $t("resources.domains.purchase_domain_remove") }}</v-btn
            >
          </div>
        </div>
      </div>
    </v-card>
    <v-card v-if="selectedDomains.length > 0">
      <v-card-title>{{ $t("resources.domains.purchase_selected_domains") }}</v-card-title>
      <div class="domain-results-wrapper">
        <div v-for="domain in selectedDomains" :key="domain['@id']" class="domain-results">
          <div>
            {{ getDomainName(domain.domain).firstPart }}<b>{{ "." + getDomainName(domain.domain).secondPart }}</b>
          </div>
          <div>
            <span class="domain-price">
              {{
                domain.available
                  ? domain.price.toLocaleString("en-US", { style: "currency", currency: "USD" }) + "/year"
                  : $t("resources.domains.purchase_domain_unavailable")
              }}
            </span>
            <v-btn
              class="select-button"
              color="primary"
              outlined
              @click="removeDomain(domain)"
              :disabled="!domain.available"
              >{{ $t("resources.domains.purchase_domain_remove") }}</v-btn
            >
          </div>
        </div>
      </div>
    </v-card>
    <div class="justify-end d-flex mt-6">
      <v-btn @click="onPurchase" color="primary" v-if="selectedDomains.length > 0">
        {{ $t("resources.domains.purchase") + ` (${selectedDomains.length})` }}
      </v-btn>
    </div>
    <Modal
      :value="showModal"
      width="400"
      @cancel="showModal = false"
      :hideCloseIcon="true"
      :title="$t('resources.domains.purchase_domain_modal_title')"
      :subtitle="$t('resources.domains.purchase_domain_modal_subtitle')">
      <div class="modal-domain-list-wrapper">
        <div v-for="domain in selectedDomains" :key="domain['@id']" class="modal-domain">
          <div class="name">{{ domain.domain }}</div>
          <div class="price">
            {{ domain.price.toLocaleString("en-US", { style: "currency", currency: "USD" }) }}
          </div>
        </div>
      </div>
      <template #actions>
        <div class="modal-actions">
          <v-btn @click="showModal = false" class="cancel" outlined>Cancel</v-btn>
          <v-btn @click="purchaseDomain" class="purchase" color="primary" :loading="purchaseLoading">{{
            $t("resources.domains.purchase") + ` (${selectedDomains.length})`
          }}</v-btn>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
  import resourceWrapper from "@/mixins/resourceWrapper";
  import { mdiSearchWeb } from "@mdi/js";
  import { dataProvider } from "@/@core/http";
  import Modal from "@/components/custom/Modal.vue";

  export default {
    name: "DomainPurchase",
    mixins: [resourceWrapper],
    components: { Modal },
    data() {
      return {
        mdiSearchWeb,
        domains: [],
        selectedDomains: [],
        loading: false,
        query: "",
        showModal: false,
        errors: "",
        purchaseLoading: false,
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
        },
      };
    },
    methods: {
      async searchDomain() {
        this.errors = [];
        if (!this.$refs.form.validate()) return;
        try {
          this.loading = true;
          const { data } = await dataProvider.customHttpCall("GET", `domains/namecheap/suggest?keywords=${this.query}`);
          this.domains = data;
        } catch (e) {
          this.errors = [e?.description] ?? this.$t("resources.domains.purchase_domain_search_error");
        } finally {
          this.loading = false;
        }
      },

      async purchaseDomain() {
        try {
          this.purchaseLoading = true;
          const { data } = await dataProvider.customHttpCall("POST", "domains/namecheap/purchase", {
            domains: this.selectedDomains.map(domain => {
              return {
                domain: domain.domain,
                price: domain.price,
              };
            }),
          });
          if (this.selectedDomains.length > 1) {
            this.$admin.toast.success(data.message);
            this.$router.push({ name: "domains_list" });
          } else {
            this.$admin.toast.success(data.message);
            this.$router.push({ name: "domains_show", params: { id: data.domains[0].id } });
          }
          this.showModal = false;
        } catch (e) {
          this.$admin.toast.error(e?.description ?? this.$t("resources.domains.purchase_domain_error"));
        } finally {
          this.purchaseLoading = false;
        }
      },

      selectDomain(domain) {
        domain.selected = true;
        this.selectedDomains.push(domain);
      },
      removeDomain(domain) {
        domain.selected = false;
        this.selectedDomains = this.selectedDomains.filter(item => item["@id"] !== domain["@id"]);
      },
      onPurchase() {
        this.showModal = true;
      },
      getDomainName(domain) {
        const parts = domain.split(".");
        const firstPart = parts.shift();
        const secondPart = parts.join(".");

        return { firstPart, secondPart };
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@/@core/preset/preset/typography/index.scss";
  @import "@/@core/preset/preset/functions";

  .input-wrapper {
    display: flex;
    .v-btn.purchase-btn {
      margin-left: 6px;
      margin-top: 19px;
      height: 46px !important;
    }
  }

  .show__heading {
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    h1 {
      @include heading-2();
      color: get-color("headline");
    }
  }

  .show__back {
    margin-right: 15px;

    .v-icon {
      color: get-color("primary");
      height: 30px;
      width: 10px;
      font-size: 32px;
      cursor: pointer;
    }
  }

  ::v-deep {
    .input-prefixed {
      .v-text-field__slot {
        input {
          margin-left: 35px;
        }
      }

      .v-input__prepend-inner {
        width: 0;
        padding-right: 0 !important;

        .v-icon {
          padding-left: 12px;
          color: get-color("icon");
        }
      }
    }
  }

  .domain-results {
    padding: 12px;
    border: 1px solid get-color("border");
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @include text-body-2();
    transition: 0.1s;

    &:hover {
      border-color: get-color("primary");
      background-color: get-color("primary-10-hover");
    }

    .domain-price {
      margin-right: 8px;
    }

    .select-button {
      transition: 0.1s !important;
    }

    .select-button:hover {
      background: get-color("primary") !important;
      color: get-color("same-white") !important;
      border-color: get-color("primary") !important;
    }
  }

  .domain-results-wrapper {
    display: flex;
    gap: 12px;
    flex-direction: column;
    margin-top: 16px;
  }

  .modal-domain-list-wrapper {
    margin-top: 16px;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 26px;
  }

  .modal-domain {
    display: flex;
    justify-content: space-between;

    .name {
      @include text-body-1();
    }

    .price {
      @include text-body-1-bold();
    }
  }

  .modal-actions {
    display: flex;
    gap: 12px;

    .purchase {
      flex: 1;
    }

    .cancel {
      flex: 1;
      border-color: get-color("icon");

      ::v-deep span {
        color: get-color("body");
      }
    }
  }
</style>
